import {Density} from "@/enum/density";

export const seoScoreStatuses = [
    'GREEN',
    'YELLOW',
    'RED'
] as const;
export type SeoScoreStatus = typeof seoScoreStatuses[number];

export const seoScoreMetricNames = [
    'aiVisibility',
    'eeatScore',
    'eatAuthor',
    'eatDate',
    'eatBio',
    'schemaMarkup',
    'faqPresent',
    'keywordVariants',
    'relatedKeywords',
    'keywordInSlug',
    'keywordInH1',
    'keywordInIntroduction',
    'keywordInH2',
    'keywordInH3',
    'keywordDensity',
    'avgWordCountOfTopTen',
    'longestWordCountOfTopTen',
    'subHeadingDistribution',
    'paragraphLength',
    'sentenceLength',
    'inspirationFromTopTen',
    'outboundLinks',
    'inboundLinks',
    'imgAltIncludesKeyword',
    'titleIncludesKeyword',
    'titleLength',
    'descriptionIncludesKeyword',
    'descriptionLength',
    'serpIsComplete',
] as const;

export type SeoScoreMetricName = typeof seoScoreMetricNames[number];
export type SeoMetricValue = boolean | number | string | Array<string>;

export interface SeoScoreMetric {
    getStatus: (currentValue: SeoMetricValue, referenceValue?: SeoMetricValue) => SeoScoreStatus;
    getTrParams?: (value: SeoMetricValue, referenceValue?: SeoMetricValue) => Array<SeoMetricValue | undefined>;
    domSelector?: string
    getClickAction?: () => void
    canAutoImprove?: boolean
}

export const seoScoreCategoryIds = [
    'all',
    'ai',
    'eeat',
    'schema',
    'headings',
    'serp',
    'keywords',
    'content',
    'readability',
    'images',
    'links',
] as const;

export type SeoScoreCategoryId = typeof seoScoreCategoryIds[number];

export interface SeoScoreCategory {
    id: SeoScoreCategoryId;
    icon: string;
    metrics: SeoScoreMetricName[] | 'all';
}

export const seoScoreCategories: SeoScoreCategory[] = [
    {id: 'all', icon: 'layers', metrics: 'all'},
    {id: 'ai', icon: 'smart_toy', metrics: ['aiVisibility']},
    {id: 'eeat', icon: 'verified_user', metrics: ['eeatScore', 'eatAuthor', 'eatDate', 'eatBio']},
    {id: 'schema', icon: 'code', metrics: ['schemaMarkup']},
    {id: 'headings', icon: 'title', metrics: ['keywordInH1', 'keywordInH2', 'keywordInH3', 'subHeadingDistribution']},
    {id: 'serp', icon: 'travel_explore', metrics: ['titleIncludesKeyword', 'titleLength', 'descriptionIncludesKeyword', 'descriptionLength', 'serpIsComplete']},
    {id: 'keywords', icon: 'center_focus_strong', metrics: ['keywordVariants', 'keywordDensity', 'relatedKeywords', 'keywordInIntroduction']},
    {id: 'content', icon: 'article', metrics: ['avgWordCountOfTopTen', 'longestWordCountOfTopTen', 'inspirationFromTopTen', 'faqPresent']},
    {id: 'readability', icon: 'format_align_left', metrics: ['paragraphLength', 'sentenceLength']},
    {id: 'images', icon: 'image', metrics: ['imgAltIncludesKeyword']},
    {id: 'links', icon: 'link', metrics: ['keywordInSlug', 'inboundLinks', 'outboundLinks']},
];

export type SeoScoreContext = {
    articleDensity: number,
    topTenGeometricAverageWordCount: number,
    topTenLongestWordCount: number,
    mergedText: string
}
export const seoScoreContext: SeoScoreContext = {
    articleDensity: Density.OK,
    topTenGeometricAverageWordCount: 0,
    topTenLongestWordCount: 0,
    mergedText: ''
};
